<script>
  import { onMount } from "svelte";

  let { onclose } = $props();

  // Configure via env: `VITE_FORMSPREE_ID` (e.g. `xeqpyvbe`). Falls back to the
  // existing endpoint so current Vercel deploys keep working without env setup.
  // `VITE_CONTACT_EMAIL` is optional — used for the mailto: fallback link.
  const formspreeId = import.meta.env.VITE_FORMSPREE_ID || "xeqpyvbe";
  const contactEmail = import.meta.env.VITE_CONTACT_EMAIL || "";
  const endpoint = `https://formspree.io/f/${formspreeId}`;

  let status = $state("idle"); // 'idle' | 'sending' | 'success' | 'error'
  let emailInput = $state(null);
  let modalEl = $state(null);

  onMount(() => {
    const previouslyFocused = document.activeElement;
    emailInput?.focus();
    return () => previouslyFocused?.focus?.();
  });

  async function handleSubmit(event) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    status = "sending";
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      status = response.ok ? "success" : "error";
    } catch {
      status = "error";
    }
  }

  function handleKeydown(event) {
    if (event.key === "Escape") {
      onclose();
      return;
    }
    // Simple focus trap: keep Tab cycling inside the modal.
    if (event.key !== "Tab" || !modalEl) return;
    const focusables = modalEl.querySelectorAll(
      'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
    );
    const list = [...focusables].filter((el) => !el.disabled);
    if (list.length === 0) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div
  class="backdrop"
  onclick={(event) => event.target === event.currentTarget && onclose()}
>
  <div
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="contact-title"
    bind:this={modalEl}
  >
    <button class="close" onclick={onclose} aria-label="Close">&times;</button>
    <h2 id="contact-title">Get in touch</h2>

    {#if status === "success"}
      <p class="note" role="status">
        Thanks for your message — I'll get back to you soon.
      </p>
    {:else}
      <form onsubmit={handleSubmit} aria-busy={status === "sending"}>
        <label for="contact-email">Email</label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          autocomplete="email"
          bind:this={emailInput}
        />

        <label for="contact-message">Message</label>
        <textarea id="contact-message" name="message" rows="5" required
        ></textarea>

        <input
          type="text"
          name="_gotcha"
          class="gotcha"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
        />

        <button class="send" type="submit" disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Send"}
        </button>

        {#if status === "error"}
          <p class="note error" role="alert">
            Something went wrong — please try again,
            {#if contactEmail}
              or email <a class="fallback-link" href={`mailto:${contactEmail}`}
                >{contactEmail}</a
              >,
            {/if}
            or reach out on
            <a class="fallback-link" href="https://www.linkedin.com/in/mnlsrv/"
              >LinkedIn</a
            >.
            <span class="note-hint"
              >(Ad-blockers sometimes block the form endpoint.)</span
            >
          </p>
        {/if}
      </form>
    {/if}
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 10;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .modal {
    position: relative;
    width: 100%;
    max-width: 44rem;
    background: #3d4759;
    border-radius: 1rem;
    padding: 3rem;
    color: white;
    box-shadow: 0 2rem 6rem rgba(0, 0, 0, 0.4);
  }

  h2 {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 2rem;
  }

  .close {
    position: absolute;
    top: 1rem;
    right: 1.4rem;
    background: none;
    border: none;
    color: #a5adbc;
    font-size: 2.8rem;
    line-height: 1;
    cursor: pointer;
  }

  .close:hover,
  .close:focus-visible {
    color: white;
  }

  label {
    display: block;
    font-size: 1.3rem;
    color: #a5adbc;
    margin-bottom: 0.6rem;
  }

  input,
  textarea {
    width: 100%;
    font: inherit;
    font-size: 1.5rem;
    color: white;
    background: #2b3342;
    border: 1px solid #566178;
    border-radius: 0.6rem;
    padding: 1rem;
    margin-bottom: 1.6rem;
  }

  textarea {
    resize: vertical;
  }

  input:focus-visible,
  textarea:focus-visible {
    outline: 2px solid #a5adbc;
    outline-offset: 1px;
  }

  .send {
    font: inherit;
    font-size: 1.5rem;
    font-weight: 600;
    background: white;
    color: #323b4c;
    border: none;
    border-radius: 0.6rem;
    padding: 1rem 2.4rem;
    cursor: pointer;
  }

  .send:hover {
    background: #dfe3ea;
  }

  .send:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .note {
    font-size: 1.5rem;
  }

  .error {
    color: #f2b8b5;
    margin-top: 1.2rem;
  }

  .fallback-link {
    color: white;
  }

  .note-hint {
    display: block;
    margin-top: 0.6rem;
    font-size: 1.3rem;
    color: #a5adbc;
  }

  /* Formspree honeypot — hidden from sighted users and AT */
  .gotcha {
    position: absolute;
    opacity: 0;
    height: 0;
    width: 0;
    padding: 0;
    margin: 0;
    border: 0;
    pointer-events: none;
  }
</style>
